// ═══════════════════════════════════════════════════════════════
// FORMAT FICHE — la fiche détail nouvelle génération (doctrine viralité).
// Design validé par Antonin le 2026-07-02 (prototype scripts/gen-fiche.js) :
// 1. Signature d'engagement : likes/commentaires/partages en ratio vs la
//    médiane de la niche, échelle de couleur (vert = sur-performe).
// 2. Le vrai moteur : signal mesuré qui ressort, sinon signal extrapolé
//    (rétention/saves/envois/...) prouvé par les commentaires.
// 3. La preuve dans les commentaires + le ressenti dominant.
// 4. Caption traduite (déroulant) + script décortiqué (vidéos à gros script).
// 5. Psycho (badges → signal) + Refais-le chez toi.
// 6. CTA "3 exemples pour MON compte" : personnalisation OPT-IN, gatée Pro.
//    La fiche par défaut reste générique, TOUJOURS.
// Données : GET /api/studio/fiche?video=<airtable_id> (copie Claude cachée 7j).
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from 'react'
import { showsTrialUx } from '../utils/offerFlags'
import { sb, API_URL } from '../utils/config'
import { studioEvents } from '../utils/metaPixel'
import { TrameContent } from '../utils/beatGuide'

const SIG_ICONS = { retention: '🎯', caption: '📖', profil: '👤', envois: '📩', saves: '🔖', follow: '➕' }

function fmtCompact(n) {
  n = +n || 0
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace('.', ',').replace(',0', '')} M`
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace('.', ',').replace(',0', '')} k`
  return String(n)
}

async function authedFetch(path, opts = {}) {
  const { data } = await sb.auth.getSession()
  const token = data?.session?.access_token
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
  })
  const out = await res.json().catch(() => null)
  if (!res.ok) throw new Error(out?.error || `HTTP ${res.status}`)
  return out
}

// La copie psych contient du <b> généré par notre backend : on n'autorise
// QUE cette balise, tout autre HTML est échappé.
function BoldText({ text }) {
  const parts = String(text || '').split(/(<b>[\s\S]*?<\/b>)/g)
  return parts.map((p, i) => {
    const m = p.match(/^<b>([\s\S]*?)<\/b>$/)
    return m ? <b key={i} className="font-bold">{m[1]}</b> : <span key={i}>{p.replace(/<[^>]+>/g, '')}</span>
  })
}

// Pastille de la signature d'engagement : ratio vs niche + brut en dessous.
function FpChip({ icon, ratio, count, label }) {
  const tier = ratio >= 1.4 ? 'good' : ratio >= 0.7 ? 'mid' : 'low'
  const styles = {
    good: { background: 'rgba(14,159,110,.13)', border: '1px solid rgba(14,159,110,.45)', color: '#0e9f6e' },
    mid: { background: '#f5f3fa', border: '1px solid #ece7f5', color: '#8b8496' },
    low: { background: 'rgba(234,88,12,.09)', border: '1px solid rgba(234,88,12,.28)', color: '#c2410c' },
  }[tier]
  return (
    <div className="rounded-xl py-2 px-1 text-center" style={{ background: styles.background, border: styles.border }}>
      <div className="text-[16px] leading-none">{icon}</div>
      <div className="text-[14px] font-extrabold leading-none mt-1" style={{ color: styles.color }}>{ratio > 0 && ratio < 0.1 ? '<0,1×' : ratio > 20 ? '20×+' : `${ratio.toFixed(1).replace('.', ',')}×`}</div>
      <div className="text-[9.5px] text-ink-faint leading-none mt-0.5">{fmtCompact(count)}</div>
      <div className="text-[9px] font-semibold text-ink-muted leading-tight mt-0.5">{label}</div>
    </div>
  )
}

// Chargement de l'analyse : messages qui tournent (la génération prend
// ~8-12 s la première fois qu'une vidéo est ouverte) + barre de progression.
const LOADING_STEPS = [
  'Merlin compare ses réactions à sa niche…',
  'Lecture des commentaires…',
  'Identification du signal qui l’a fait exploser…',
  'Écriture de la fiche…',
]
function Skeleton() {
  const [step, setStep] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setStep(s => Math.min(s + 1, LOADING_STEPS.length - 1)), 3000)
    return () => clearInterval(id)
  }, [])
  return (
    <div className="px-3 py-4 space-y-3" aria-busy="true">
      <div className="flex items-center gap-1.5 text-[12px] font-bold" style={{ color: '#d97206' }}>
        <span className="inline-block w-3.5 h-3.5 border-2 border-orange/30 border-t-orange rounded-full animate-spin shrink-0" />
        {LOADING_STEPS[step]}
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)' }}>
        <div className="h-full rounded-full transition-all duration-[3000ms]"
          style={{ width: `${18 + step * 26}%`, background: 'linear-gradient(90deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }} />
      </div>
      {[72, 120, 88].map((h, i) => (
        <div key={i} className="rounded-2xl animate-pulse" style={{ height: h, background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.07)' }} />
      ))}
    </div>
  )
}

// Texte à trous : les [Variables] deviennent des chips orange (comme partout).
function VarText({ text }) {
  if (!text) return null
  return text.split(/(\[[^\]]+\])/g).map((p, i) =>
    p.startsWith('[') && p.endsWith(']')
      ? <span key={i} className="inline-block px-1.5 py-0.5 mx-0.5 rounded-md text-[0.95em] font-bold"
          style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.14)', color: '#d97206' }}>{p}</span>
      : <span key={i}>{p}</span>
  )
}

// Le backend coupe les items (trame) et les commentaires extraits à 90
// caractères : on signale la troncature (demande Antonin 27/07) au lieu de
// laisser une phrase coupée net, en retirant le dernier mot partiel.
function markTruncated(s, cap = 90) {
  const t = String(s || '')
  return t.length >= cap ? t.replace(/\s*\S*$/, '').trimEnd() + ' (…)' : t
}

export default function FormatFiche({ format, idx, videoSlot, entitled = false, preload = false }) {
  const src = format?.source || {}
  const videoKey = src.airtable_id || null
  const [fiche, setFiche] = useState(null)
  const [error, setError] = useState(null)
  const [perso, setPerso] = useState(null)        // { examples_perso: [] }
  const [persoLoading, setPersoLoading] = useState(false)
  const [persoError, setPersoError] = useState(null)
  // Sous-onglets : Analyse (la fiche, défaut) / À adapter (texte à trous) / Plans (la trame)
  const [tab, setTab] = useState('analyse')
  const [saveState, setSaveState] = useState(null) // null | saving | saved | error

  // Sauvegarde le format + les exemples perso dans la bibliothèque (studio_saves)
  async function saveToLibrary() {
    if (saveState === 'saving' || saveState === 'saved') return
    setSaveState('saving')
    try {
      await authedFetch('/api/studio/saves', {
        method: 'POST',
        body: JSON.stringify({
          format_id: fiche?.video?.id || format?.format_id || null,
          format_data: { ...format, examples_perso: perso?.examples_perso || [], fiche_title: fiche?.copy?.title || null },
        }),
      })
      setSaveState('saved')
      try { studioEvents.tabClicked?.({ tab: 'fiche_save_examples', format_idx: idx, source_page: 'format_fiche' }) } catch { /* tracking */ }
    } catch {
      setSaveState('error')
    }
  }

  const [loading, setLoading] = useState(false)

  // ── Outils ADMIN (Antonin) : copier le lien + télécharger le MP4 depuis
  // n'importe quelle fiche (pour les montages). Invisible pour les users.
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminDl, setAdminDl] = useState(false)
  const [adminCopied, setAdminCopied] = useState(false)
  useEffect(() => {
    sb.auth.getSession().then(({ data }) => {
      setIsAdmin((data?.session?.user?.email || '').toLowerCase() === 'antonin.girardeau.pro@gmail.com')
    }).catch(() => { /* pas connecté */ })
  }, [])
  function adminCopyLink() {
    const lien = fiche?.video?.lien
    if (!lien) return
    try { navigator.clipboard.writeText(lien); setAdminCopied(true); setTimeout(() => setAdminCopied(false), 1500) } catch { /* clipboard indispo */ }
  }
  async function adminDownload() {
    const v = fiche?.video
    if (!v || adminDl) return
    setAdminDl(true)
    try {
      const m = (v.lien || '').match(/\/(?:reel|reels|p|tv)\/([A-Za-z0-9_-]+)/)
      let blob, name
      if (m) {
        // Instagram : URL fraîche + stream via l'endpoint admin (JWT)
        const { data } = await sb.auth.getSession()
        const res = await fetch(`${API_URL}/api/admin/video-download?shortcode=${m[1]}`, {
          headers: { Authorization: `Bearer ${data?.session?.access_token}` },
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        blob = await res.blob(); name = `${m[1]}.mp4`
      } else {
        // TikTok : URL MP4 fraîche via l'endpoint public (tikwm)
        const d = await authedFetch(`/api/format/${v.id}/video`)
        const res = await fetch(d.url)
        if (!res.ok) { window.open(d.url, '_blank'); return }
        blob = await res.blob(); name = `${v.airtable_id || v.id}.mp4`
      }
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob); a.download = name
      document.body.appendChild(a); a.click()
      setTimeout(() => a.remove(), 2000)
    } catch {
      // dernier recours : ouvrir le post d'origine
      if (fiche?.video?.lien) window.open(fiche.video.lien, '_blank')
    } finally { setAdminDl(false) }
  }

  // Chargement en 2 temps mais AUTOMATIQUE (demande Antonin 14/07 : plus de
  // clic « Pourquoi ça fonctionne ? ») : les stats légères peignent la fiche
  // tout de suite, et l'analyse complète part EN PARALLÈLE dès l'ouverture
  // (elle est identique pour tous les users et cachée côté serveur — seul le
  // tout premier affichage d'une vidéo paie la génération).
  useEffect(() => {
    setFiche(null); setError(null); setPerso(null); setPersoError(null); setLoading(!preload); setTab('analyse')
    if (!videoKey) { setError('missing_video'); return }
    let alive = true
    if (!preload) {
      authedFetch(`/api/studio/fiche?video=${encodeURIComponent(videoKey)}&stats=1`)
        .then(d => { if (alive) setFiche(prev => (prev && !prev.stats_only) ? prev : d) })
        .catch(() => { /* la requête complète ci-dessous porte l'erreur */ })
    }
    authedFetch(`/api/studio/fiche?video=${encodeURIComponent(videoKey)}`)
      .then(d => { if (alive) { setFiche(d); setLoading(false) } })
      .catch(e => { if (alive) { setError(e.message); setLoading(false) } })
    return () => { alive = false }
  }, [videoKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadFicheRef = useRef(null)
  useEffect(() => {
    function onTourLoad() { try { loadFicheRef.current?.() } catch { /* no-op */ } }
    window.addEventListener('studio:tour-load-fiche', onTourLoad)
    return () => window.removeEventListener('studio:tour-load-fiche', onTourLoad)
  }, [])

  function loadFiche() {
    if (!videoKey) { setError('missing_video'); return }
    if ((fiche && !fiche.stats_only) || loading) return
    setLoading(true); setError(null)
    try { studioEvents.tabClicked?.({ tab: 'fiche_load_analysis', format_idx: idx, source_page: 'format_fiche' }) } catch { /* tracking */ }
    authedFetch(`/api/studio/fiche?video=${encodeURIComponent(videoKey)}`)
      .then(d => setFiche(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }
  loadFicheRef.current = loadFiche

  // L'onglet Plans a besoin des étapes : les stats légères les portent déjà
  // (beats), sinon on déclenche le chargement complet.
  useEffect(() => {
    if (tab === 'plans' && !fiche?.beats?.length && !loading && !(format?.beats_raw?.length || format?.structure?.length)) loadFiche()
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  async function personalize() {
    try { studioEvents.tabClicked?.({ tab: 'fiche_personalize', format_idx: idx, source_page: 'format_fiche' }) } catch { /* tracking only */ }
    if (!entitled) {
      try { window.dispatchEvent(new CustomEvent('studio:open-upgrade')) } catch { /* no-op */ }
      return
    }
    setPersoLoading(true); setPersoError(null)
    try {
      const d = await authedFetch('/api/studio/fiche/personalize', { method: 'POST', body: JSON.stringify({ video: videoKey }) })
      try { window.dispatchEvent(new CustomEvent('studio:examples-generated')) } catch { /* tracker */ }
      setPerso(d)
    } catch (e) {
      setPersoError(e.message === 'no_persona' ? 'Lance d’abord un scan de ton compte pour la personnalisation.' : 'Impossible de générer tes exemples, réessaie.')
    } finally {
      setPersoLoading(false)
    }
  }

  const c = fiche?.copy || {}
  const R = fiche?.ratios || { like: 0, comment: 0, share: 0 }
  const counts = fiche?.counts || {}
  const over = fiche ? [
    { key: 'comment', icon: '💬', label: 'Elle force le commentaire', r: R.comment },
    { key: 'share', icon: '🔁', label: 'Elle fait partager', r: R.share },
    { key: 'like', icon: '❤️', label: 'Elle fait liker', r: R.like },
    // Saves : mesurées sur TikTok seulement — R.save est null quand la donnée
    // n'existe pas (jamais affiché), le filtre >= 1.4 l'écarte naturellement.
    { key: 'save', icon: '🔖', label: 'Elle fait enregistrer', r: R.save ?? 0 },
  ].filter(s => s.r >= 1.4).sort((a, b) => b.r - a.r) : []
  const ex = c.extrapolated || {}

  return (
    <article className="merlin-format-card flex flex-col overflow-hidden">
      {/* ═══ Header : chips + titre + premise ═══ */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          {/* Badge matching honnête (Antonin 27/07) : « dans ta niche » vs
              « autre niche à adapter » — on ne prétend jamais que c'est sa
              niche quand ce n'est pas le cas. */}
          {typeof format?.cross === 'boolean' && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={format.cross
                ? { background: 'rgba(254,130,4,.14)', color: '#c2410c' }
                : { background: 'rgba(16,159,110,.12)', color: '#0e9f6e' }}>
              {format.cross ? '↔ Autre niche, à adapter' : '✓ Dans ta niche'}
            </span>
          )}
          {src.vues > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),.12)', color: 'var(--merlin-accent, #fe8204)' }}>
              {fmtCompact(src.vues)} vues
            </span>
          )}
          {/* Saves : mesurées sur TikTok uniquement — chip absente quand la donnée n'existe pas */}
          {(counts.save || src.enregistrements) > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(22,163,74,.1)', color: '#16a34a' }}>
              🔖 {fmtCompact(counts.save || src.enregistrements)} saves
            </span>
          )}
          {(fiche?.video?.duree || src.duree_secondes) && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),.1)', color: 'var(--merlin-violet, #7c3aed)' }}>
              {fiche?.video?.duree || src.duree_secondes} secondes
            </span>
          )}
          {(src.niche || fiche?.video?.niche) && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),.1)', color: 'var(--merlin-violet, #7c3aed)' }}>
              {src.niche || fiche?.video?.niche}
            </span>
          )}
        </div>
        <h3 className="text-[16px] font-extrabold text-ink leading-tight">
          {c.title || src.format_label || src.format_nom || 'Format viral'}
        </h3>
        {c.premise && <p className="text-[12px] text-ink-muted leading-snug mt-1">{c.premise}</p>}
      </div>

      {/* ═══ Comment le refaire pour toi (transposition — Antonin 27/07) ═══
          Rend l'adaptabilité VISIBLE : l'angle exact pour refaire ce format
          dans SA niche. C'est ce qui tue le « je ne m'y reconnaissais pas ». */}
      {format?.transpo && (
        <div className="mx-3 mb-1 rounded-xl px-3 py-2.5" style={{ background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', border: '1px solid #fed7aa' }}>
          <p className="text-[9.5px] uppercase tracking-wider font-extrabold mb-0.5" style={{ color: '#c2620a' }}>Comment le refaire pour toi</p>
          <p className="text-[13px] font-bold leading-snug" style={{ color: '#9a3412' }}>{format.transpo}</p>
        </div>
      )}

      {/* ═══ Vidéo (player existant, injecté par le parent) ═══ */}
      {videoSlot}

      {/* ═══ Sous-onglets : Analyse (défaut) / À adapter / Plans ═══ */}
      <div className="px-3 pt-2.5">
        <div className="flex gap-0.5 p-0.5 rounded-lg" style={{ background: 'rgba(26,16,64,0.05)' }}>
          {[
            { v: 'analyse', l: '🔍 Analyse' },
            { v: 'adapter', l: '✏️ À adapter' },
            { v: 'plans', l: '📐 Plans' },
          ].map(o => (
            <button key={o.v} type="button"
              onClick={() => { setTab(o.v); try { studioEvents.tabClicked?.({ tab: `fiche_${o.v}`, format_idx: idx, source_page: 'format_fiche' }) } catch { /* tracking */ } }}
              className={`flex-1 text-[11px] px-2 py-1.5 rounded-md font-bold transition-colors ${tab === o.v ? 'bg-white shadow-sm text-ink' : 'text-ink-muted'}`}>
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ Onglet À ADAPTER : le texte à trous (variables) ═══ */}
      {tab === 'adapter' && (
        <div className="px-3 py-3">
          {(format?.hook_template || fiche?.video?.hook_template) && (
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-ink-muted mb-1.5">Le hook à trous</p>
              <p className="text-[13px] font-bold text-ink leading-snug italic rounded-xl p-3" style={{ border: '1px solid #ece7f5' }}>
                « <VarText text={format?.hook_template || fiche?.video?.hook_template} /> »
              </p>
            </div>
          )}
          {format?.script_template ? (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-ink-muted mb-1.5">Le script à trous</p>
              <p className="text-[12px] text-ink leading-relaxed whitespace-pre-line rounded-xl p-3" style={{ border: '1px solid #ece7f5' }}>
                <VarText text={format.script_template} />
              </p>
            </div>
          ) : (
            <p className="text-[11.5px] text-ink-faint">Remplace les blocs orange par tes propres éléments : c&rsquo;est la partie interchangeable du format, le reste est sa mécanique.</p>
          )}
        </div>
      )}

      {/* ═══ Onglet PLANS : la trame étape par étape ═══ */}
      {tab === 'plans' && (
        <div className="px-3 py-3">
          <TrameContent
            beats={(format?.beats_raw?.length ? format.beats_raw : null) || (format?.structure?.length ? format.structure : null) || fiche?.beats || []}
            mode={format?.texte_ecran_mode || format?.source?.texte_ecran_mode || fiche?.video?.texte_ecran_mode} />
          {!fiche && !error && (format?.beats_raw?.length || format?.structure?.length) == null && (
            <p className="text-[11px] text-ink-faint mt-2 flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 border-2 border-orange/30 border-t-orange rounded-full animate-spin" /> Chargement de la trame…
            </p>
          )}
        </div>
      )}

      {tab === 'analyse' && error && (
        <div className="px-3 py-4">
          <p className="text-[12px] text-ink-muted">L&rsquo;analyse de cette vidéo n&rsquo;est pas disponible pour le moment.</p>
        </div>
      )}
      {/* Chargement au mount : narratif si on précharge l'analyse complète
          (Claude ~8-12 s à froid), mini-pastilles labellisées pour les stats. */}
      {tab === 'analyse' && !fiche && !error && (
        preload ? <div className="px-3 py-4"><Skeleton /></div> : (
          <div className="px-3 py-4" aria-busy="true">
            <p className="text-[11.5px] font-bold mb-2 flex items-center gap-1.5" style={{ color: '#d97206' }}>
              <span className="inline-block w-3 h-3 border-2 border-orange/30 border-t-orange rounded-full animate-spin shrink-0" />
              Chargement des stats d&rsquo;engagement…
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {[0, 1, 2].map(i => <div key={i} className="rounded-xl animate-pulse h-[70px]" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.07)' }} />)}
            </div>
          </div>
        )
      )}

      {tab === 'analyse' && fiche && (
        <div className="px-3 pb-3">
          {/* Barre ADMIN : lien + MP4 pour les montages (Antonin uniquement) */}
          {isAdmin && fiche.video?.lien && (
            <div className="mt-3 flex items-center gap-1.5 rounded-xl px-2.5 py-2" style={{ background: 'rgba(15,10,26,0.04)', border: '1px dashed rgba(15,10,26,0.18)' }}>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-ink-faint mr-auto">Admin</span>
              <button type="button" onClick={adminCopyLink}
                className="text-[11px] font-bold px-2.5 py-1.5 rounded-md hover:opacity-80"
                style={{ background: 'rgba(15,10,26,0.06)', border: '1px solid rgba(15,10,26,0.12)' }}
                title={fiche.video.lien}>
                {adminCopied ? '✓ copié' : '🔗 Copier le lien'}
              </button>
              <button type="button" onClick={adminDownload} disabled={adminDl}
                className="text-[11px] font-bold px-2.5 py-1.5 rounded-md hover:opacity-80 disabled:opacity-40"
                style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                title="Télécharger le MP4 (pour les montages)">
                {adminDl ? '⏳…' : '⬇️ MP4'}
              </button>
            </div>
          )}
          {/* ═══ 1. Pourquoi elle explose ═══ */}
          <div className="mt-3" data-tour="fiche-signature">
            <p className="text-[10px] uppercase tracking-wider font-extrabold text-ink-muted mb-2">Pourquoi elle explose</p>
            <div className="rounded-2xl p-3" style={{ background: 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),.1), rgba(var(--merlin-violet-rgb, 124,58,237),.09))', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),.28)' }}>
              {/* Signature d'engagement : réactions mesurées vs la niche.
                  Saves (TikTok) : 4e pastille SEULEMENT quand la donnée existe
                  (R.save === null = pas mesurée, on n'affiche jamais un faux 0). */}
              {/* Doctrine données masquées : une réaction masquée/désactivée
                  (count null ou 0 sur une vidéo virale = stat cachée) ne
                  s'affiche JAMAIS en faux 0 — la pastille disparaît. */}
              {(() => {
                const chips = [
                  ['❤️', R.like, counts.like, 'likes'],
                  ['💬', R.comment, counts.comment, 'commentaires'],
                  ['🔁', R.share, counts.share, 'partages'],
                  ['🔖', R.save, counts.save, 'saves'],
                ].filter(([, r, c]) => r != null && c)
                if (!chips.length) return null
                const colsClass = ['', 'grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'][Math.min(4, chips.length)]
                return (
                  <div className={`grid gap-1.5 mb-1.5 ${colsClass}`}>
                    {chips.map(([icon, r, c, label]) => <FpChip key={label} icon={icon} ratio={r} count={c} label={label} />)}
                  </div>
                )
              })()}
              <p className="text-[10.5px] text-ink-muted mb-2.5">
                Chaque réaction comparée à la moyenne de sa niche. <b style={{ color: '#0e9f6e' }}>Vert</b> = elle sur-performe.
              </p>

              {/* Le(s) signal(aux) mesuré(s) qui ressortent */}
              {over.map(s => (
                <div key={s.key} className="flex gap-2.5 items-start bg-white rounded-xl p-2.5 mb-2" style={{ border: '1px solid #ece7f5' }}>
                  <span className="text-[18px] leading-none">{s.icon}</span>
                  <div>
                    <p className="text-[17px] font-extrabold leading-none" style={{ color: 'var(--merlin-accent, #fe8204)' }}>{s.r.toFixed(s.r >= 10 ? 0 : 1)}× sa niche</p>
                    <p className="text-[12px] font-bold text-ink mt-0.5">{s.label}</p>
                  </div>
                </div>
              ))}

              {/* Sinon : le moteur extrapolé, prouvé par les commentaires */}
              {over.length === 0 && ex.label && (
                <div className="flex gap-2.5 items-start rounded-xl p-2.5 mb-2" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),.06)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),.25)' }}>
                  <span className="text-[18px] leading-none">{SIG_ICONS[ex.key] || '🎯'}</span>
                  <div>
                    <p className="text-[15px] font-extrabold leading-tight" style={{ color: 'var(--merlin-violet, #7c3aed)' }}>{ex.label}</p>
                    <p className="text-[11px] font-bold text-ink mt-0.5">Le vrai moteur de la vidéo</p>
                    {ex.how && <p className="text-[11.5px] text-ink-muted mt-1 leading-snug">{ex.how}</p>}
                  </div>
                </div>
              )}

              {/* La preuve, dans les commentaires : menu déroulant (retour
                  Antonin 10/07), fermé par défaut, compteur visible. */}
              {fiche.comments?.length > 0 && (
                <details className="mt-2.5 group">
                  <summary className="cursor-pointer list-none flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-extrabold text-ink-muted select-none">
                    <span className="transition-transform group-open:rotate-90 text-[9px]">▶</span>
                    💬 La preuve, dans les commentaires ({fiche.comments.length})
                  </summary>
                  <div className="space-y-1.5 mt-1.5">
                    {fiche.comments.map((com, i) => (
                      <div key={i} className="bg-white rounded-xl px-2.5 py-2 text-[12px]" style={{ border: '1px solid #ece7f5' }}>
                        {com.likes && <span className="float-right text-[10px] text-ink-faint ml-2">{com.likes}</span>}
                        {com.user && <b className="font-bold mr-1" style={{ color: 'var(--merlin-violet, #7c3aed)' }}>@{com.user}</b>}
                        {markTruncated(com.text)}
                      </div>
                    ))}
                    {fiche.sentiment && (
                      <p className="text-[11px] text-ink-muted pt-0.5">Le ressenti dominant : <b className="text-ink">{fiche.sentiment}</b>.</p>
                    )}
                  </div>
                </details>
              )}
              {!fiche.comments?.length && fiche.sentiment && (
                <p className="text-[11px] text-ink-muted mt-2">Le ressenti dominant : <b className="text-ink">{fiche.sentiment}</b>.</p>
              )}
            </div>
          </div>

          {/* L'analyse complète se charge toute seule à l'ouverture : ce
              bouton ne sert plus que de RELANCE si la requête a échoué. */}
          {fiche.stats_only && !loading && (
            <div className="mt-3" data-tour="fiche-why">
              <button type="button" onClick={loadFiche}
                className="w-full py-3.5 rounded-xl text-white font-extrabold text-[14px] transition-transform hover:scale-[1.01]"
                style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-soft, #9f67ff))', boxShadow: '0 6px 18px rgba(var(--merlin-violet-rgb, 124,58,237),0.35)' }}>
                🔍 Pourquoi ça fonctionne ?
              </button>
              <p className="text-[10.5px] text-ink-faint text-center mt-1.5">Merlin décortique le signal viral, les commentaires et la psychologie de cette vidéo.</p>
            </div>
          )}
          {fiche.stats_only && loading && <div className="mt-3"><Skeleton /></div>}

          {/* ═══ 2. Description sous la vidéo (déroulant) : ORIGINALE + traduction
              (demande Antonin 14/07 : les deux versions, pas seulement la FR) ═══ */}
          {(c.caption_fr?.trim() || fiche.video?.description?.trim()) && (
            // CAPTION RICHE = DÉPLIÉE D'OFFICE (retour Antonin 21/07 : certains
            // formats sont CENTRÉS sur la description, la cacher derrière un
            // déplier rendait le format incompréhensible).
            <details open={((fiche.video?.description || c.caption_fr || '').length > 120)}
              className="mt-3 rounded-2xl overflow-hidden" style={{ border: '1px solid #ece7f5' }}>
              <summary className="cursor-pointer list-none px-3 py-2.5 text-[12.5px] font-extrabold text-ink flex justify-between items-center">
                📄 La description sous la vidéo <span className="text-[10px] font-semibold text-ink-faint">déplier</span>
              </summary>
              <div className="px-3 pb-3 space-y-2.5">
                {fiche.video?.description?.trim() && (
                  <div>
                    <p className="text-[9.5px] uppercase tracking-wider font-bold text-ink-faint mb-0.5">Originale</p>
                    <p className="text-[12px] text-ink-muted leading-snug whitespace-pre-wrap">{fiche.video.description}</p>
                  </div>
                )}
                {c.caption_fr?.trim() && c.caption_fr.trim() !== fiche.video?.description?.trim() && (
                  <div>
                    <p className="text-[9.5px] uppercase tracking-wider font-bold text-ink-faint mb-0.5">Traduction</p>
                    <p className="text-[12px] text-ink-muted leading-snug">{c.caption_fr}</p>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* ═══ 3. Script décortiqué (vidéos portées par leur texte) ═══ */}
          {fiche.loops?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-ink-muted mb-2">Le script décortiqué</p>
              <div className="rounded-2xl p-3" style={{ border: '1px solid #ece7f5' }}>
                {fiche.loops.map((loop, i) => (
                  <div key={i} className="flex gap-2 mb-2 last:mb-0">
                    <span className="shrink-0 text-[9px] font-extrabold px-2 py-0.5 rounded-full h-fit mt-0.5" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),.1)', color: 'var(--merlin-violet, #7c3aed)' }}>{loop.fonction}</span>
                    <span className="text-[11.5px] text-ink leading-snug">{markTruncated(loop.texte)}</span>
                  </div>
                ))}
                {fiche.script && (
                  <details className="mt-1">
                    <summary className="cursor-pointer list-none py-1.5 text-[11.5px] font-extrabold text-ink flex items-center gap-1">
                      📝 Lire le script en entier <span className="text-[10px] font-semibold text-ink-faint">déplier</span>
                    </summary>
                    <p className="text-[11.5px] text-ink-muted leading-relaxed whitespace-pre-wrap pt-1">{fiche.script}</p>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* ═══ 4. Ce qui déclenche ça (dans la tête) ═══ */}
          {c.psych?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-ink-muted mb-2">Ce qui déclenche ça (dans la tête)</p>
              <div className="rounded-2xl p-3" style={{ border: '1px solid #ece7f5' }}>
                {c.psych.slice(0, 3).map((p, i) => (
                  <div key={i} className="flex gap-2.5 mb-2.5 last:mb-0">
                    <span className="shrink-0 w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-extrabold mt-0.5" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),.12)', color: 'var(--merlin-violet, #7c3aed)' }}>{i + 1}</span>
                    <div className="text-[12.5px] text-ink leading-snug">
                      <BoldText text={p.text} />
                      {p.badge && (
                        <span className="inline-block ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold align-middle" style={{ background: 'rgba(14,159,110,.12)', color: '#0e9f6e' }}>{p.badge}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ 5. Refais-le chez toi ═══ */}
          {c.recreate?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-ink-muted mb-2">Refais-le chez toi</p>
              <div className="rounded-2xl p-3" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),.05)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),.18)' }}>
                <ol className="list-decimal pl-4 space-y-2">
                  {c.recreate.map((r, i) => (
                    <li key={i} className="text-[12.5px] text-ink leading-snug">
                      {r}
                      {i === 0 && c.example && (
                        <span className="block mt-0.5 text-[12px] italic" style={{ color: 'var(--merlin-violet, #7c3aed)' }}>Ex : « {c.example} »</span>
                      )}
                    </li>
                  ))}
                </ol>
                {c.warning && (
                  <p className="mt-2.5 text-[12px] rounded-lg px-2.5 py-2" style={{ background: '#fff7ed', color: '#9a3412' }}>⚠️ {c.warning}</p>
                )}
              </div>
            </div>
          )}

          {/* ═══ 6. Personnalisation OPT-IN (gatée Pro) : après l'analyse ═══ */}
          {fiche.stats_only ? null : perso?.examples_perso?.length > 0 ? (
            <div className="mt-3" data-tour="fiche-perso">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-ink-muted mb-2">✨ 3 exemples pour TON compte</p>
              <div className="space-y-2.5">
                {perso.examples_perso.map((exo, i) => (
                  <div key={i} className="rounded-2xl p-3" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),.06)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),.25)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="shrink-0 w-[20px] h-[20px] rounded-full flex items-center justify-center text-[10px] font-extrabold" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),.16)', color: 'var(--merlin-accent, #fe8204)' }}>{i + 1}</span>
                      <p className="text-[12.5px] font-extrabold text-ink leading-tight">{exo.titre || `Angle ${i + 1}`}</p>
                    </div>
                    {exo.hook && (
                      <div className="bg-white rounded-xl px-2.5 py-2 mb-2" style={{ border: '1px solid #ece7f5' }}>
                        <p className="text-[9.5px] uppercase tracking-wider font-extrabold mb-0.5" style={{ color: 'var(--merlin-accent, #fe8204)' }}>Le hook</p>
                        <p className="text-[12.5px] font-bold text-ink italic leading-snug">« {exo.hook} »</p>
                      </div>
                    )}
                    {exo.etapes?.length > 0 && (
                      <div className="space-y-1">
                        {exo.etapes.map((et, j) => (
                          <p key={j} className="text-[12px] text-ink leading-snug flex gap-1.5">
                            <span className="shrink-0 font-extrabold" style={{ color: '#0e9f6e' }}>✓</span>{et}
                          </p>
                        ))}
                      </div>
                    )}
                    {exo.cta?.trim() && (
                      <p className="text-[11.5px] text-ink-muted mt-1.5"><b className="text-ink">CTA :</b> {exo.cta}</p>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={saveToLibrary} disabled={saveState === 'saving' || saveState === 'saved'}
                className="mt-3 w-full py-3 rounded-xl font-extrabold text-[13.5px] transition-transform hover:scale-[1.01] disabled:opacity-80"
                style={saveState === 'saved'
                  ? { background: 'rgba(14,159,110,.12)', color: '#0e9f6e', border: '1px solid rgba(14,159,110,.4)' }
                  : { background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', color: '#fff', boxShadow: '0 6px 18px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
                {saveState === 'saved' ? '✓ Sauvegardé dans ta bibliothèque' : saveState === 'saving' ? 'Sauvegarde…' : '💾 Sauvegarder dans ma bibliothèque'}
              </button>
              {saveState === 'error' && <p className="text-[11px] text-center mt-1" style={{ color: '#c2410c' }}>Impossible de sauvegarder, réessaie.</p>}
            </div>
          ) : (
            <div className="mt-4" data-tour="fiche-perso">
              <button type="button" onClick={personalize} disabled={persoLoading}
                className="w-full py-3 rounded-xl text-white font-extrabold text-[14px] transition-transform hover:scale-[1.01] disabled:opacity-70"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 6px 18px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
                {persoLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Merlin adapte le format à ton compte…
                  </span>
                ) : '✨ 3 exemples pour MON compte'}
              </button>
              <p className="text-[10.5px] text-ink-faint text-center mt-1.5">
                {entitled ? 'Adaptés à ta niche et ton ton.' : (showsTrialUx() ? 'Adaptés à ta niche et ton ton. Inclus dans l’essai Pro.' : 'Adaptés à ta niche et ton ton. Inclus dans Merlin Pro.')}
              </p>
              {persoError && <p className="text-[11px] text-center mt-1" style={{ color: '#c2410c' }}>{persoError}</p>}
            </div>
          )}

          <FicheFeedbackBar format={format} />
        </div>
      )}
    </article>
  )
}

// ─── Feedback matching (demande Antonin 12/07) : « ce format te correspond ? »
// Chaque réponse part dans user_events (studio_format_feedback) avec la niche
// source du format : c'est la matière pour apprendre quels formats vont à
// quelles niches (l'analyse des 16 résiliations a montré que le mauvais
// matching de la traîne de formats est une cause directe de churn).
function FicheFeedbackBar({ format }) {
  const src = format?.source || {}
  const fbKey = `studio_format_fb_${format?.format_id || src.airtable_id || src.lien || 'x'}`
  const [verdict, setVerdict] = useState(() => { try { return localStorage.getItem(fbKey) } catch { return null } })
  function send(v) {
    setVerdict(v)
    try { localStorage.setItem(fbKey, v) } catch {}
    try {
      studioEvents.formatFeedback({
        format_id: format?.format_id || null,
        airtable_id: src.airtable_id || null,
        format_nom: src.format_nom || src.format_label || null,
        source_niche: src.niche || null,
        verdict: v,
      })
    } catch {}
  }
  return (
    <div className="mt-4 rounded-2xl px-3 py-2.5" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),.05)', border: '1px solid #ece7f5' }}>
      {verdict ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] font-bold leading-snug" style={{ color: verdict === 'adapted' ? '#0e9f6e' : 'var(--merlin-violet, #7c3aed)' }}>
            {verdict === 'adapted' ? '✓ Noté ! On t’en proposera d’autres dans ce style.' : '✓ Noté ! On évitera ce style pour ton compte.'}
          </p>
          <button type="button" onClick={() => { setVerdict(null); try { localStorage.removeItem(fbKey) } catch {} }}
            className="shrink-0 text-[11px] font-bold text-ink-faint underline">changer</button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] font-bold text-ink leading-snug">Ce format correspond à ton compte ?</p>
          <div className="flex gap-1.5 shrink-0">
            <button type="button" onClick={() => send('adapted')}
              className="px-3 py-1.5 rounded-full text-[12px] font-extrabold transition-transform hover:scale-105"
              style={{ background: 'rgba(14,159,110,.12)', color: '#0e9f6e', border: '1px solid rgba(14,159,110,.35)' }}>
              👍 Oui
            </button>
            <button type="button" onClick={() => send('not_adapted')}
              className="px-3 py-1.5 rounded-full text-[12px] font-extrabold transition-transform hover:scale-105"
              style={{ background: 'rgba(194,65,12,.08)', color: '#c2410c', border: '1px solid rgba(194,65,12,.3)' }}>
              👎 Pas adapté
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
